/**
 * Cell-scoped error boundary (Epic nmbrs-verzuim-kalender, Story 1.3
 * code-review finding — Blind Hunter + Edge Case Hunter, both
 * independently).
 *
 * `AanwezigheidCell`'s `HalfCell` deliberately `throw`s for any
 * `AttendanceDagdeelStatus` value it doesn't have an explicit render for
 * (AC 7 — a silent fallback to "werkt"/"Kantoor" is exactly the bug class
 * this guards against). But `packages/frontend/src` has NO app-level
 * `ErrorBoundary` anywhere — without this wrapper, that throw would
 * unmount the ENTIRE attendance grid (every employee × every date), not
 * just the one cell with the unrecognized status (e.g. a stale cached
 * response from before a status was added, mid-deploy version skew).
 *
 * This boundary scopes the blast radius to exactly one grid cell: every
 * OTHER cell keeps rendering normally. The fallback deliberately uses the
 * reserved error-red (`#D64545`) — this is the one place on this screen
 * where that color IS the correct signal (a genuine rendering error, not
 * a status to display).
 */

import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class CellErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown): void {
    // Deliberate: this is the only signal a developer gets that a status
    // render was rejected.
    console.error('AanwezigheidCell rendering error (contained to one cell):', error);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          role="gridcell"
          aria-label="Fout bij weergeven"
          className="flex items-center justify-center h-10 rounded border px-1"
          style={{ backgroundColor: '#FEF2F2', borderColor: '#D64545' }}
          title="Kon deze cel niet weergeven"
        >
          <span className="text-[9px] font-medium truncate" style={{ color: '#D64545' }}>
            Fout
          </span>
        </div>
      );
    }
    return this.props.children;
  }
}
